import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Award, Phone, MapPin,
  Lock, LogOut, Plus, Pencil, Trash2, Upload, X, Save, Eye, EyeOff, Settings, Star, FileText, Cake, Search,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import RatingsTimeline from './RatingsTimeline';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WORK_PROFILES = ['Excellent', 'Very Good', 'Good', 'Average'];

// View / Print an individual payslip, auto-filled from payroll entries
const PayslipSection = ({ employeeId }) => {
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/payroll/periods-for-employee`, { params: { employee_id: employeeId } });
        if (!active) return;
        const ps = data.periods || [];
        setPeriods(ps); setPeriod(ps[0] || '');
      } catch { /* no payroll */ }
    })();
    return () => { active = false; };
  }, [employeeId]);
  const view = async () => {
    if (!period) return;
    setBusy(true); setErr('');
    try {
      const res = await axios.get(`${API}/admin/payroll/payslip`, { params: { employee_id: employeeId, pay_period: period }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { setErr('No payslip available for this period'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 pt-3 border-t border-gray-100" data-testid="payslip-section">
      <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase mb-2">Payslip · View / Print</div>
      {periods.length === 0 ? (
        <p className="text-[11px] text-gray-400">No payroll entries yet for this employee.</p>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="admin-input max-w-[160px]" data-testid="payslip-period">
            {periods.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <Button type="button" onClick={view} disabled={busy} className="rounded-none bg-gray-900 hover:bg-black text-white px-4" data-testid="view-payslip-btn">
            <FileText size={14} className="mr-1.5" /> {busy ? 'Opening…' : 'View / Print Payslip'}
          </Button>
        </div>
      )}
      {err && <p className="text-[11px] text-red-500 mt-1">{err}</p>}
    </div>
  );
};

const DESIGNATIONS = [
  'Founder Partner',
  'Senior Partner',
  'Partner',
  'Associate',
  'Manager',
  'Senior Accountant',
  'Senior Executive',
  'Senior Article',
  'Junior Article',
  'Junior Executive',
  'Receptionist',
  'Attendant/Office Assistant',
];

const SPECIALIZATIONS = [
  'Audit & Assurance',
  'Taxation DT & IDT',
  'Compliances',
  'Accounting & Bookkeeping',
  'Litigation',
  'Consulting',
];

const RATING_CATEGORIES = [
  { key: 'office_timings',   label: 'Office Timings & Policy Adherence' },
  { key: 'task_performance', label: 'Task Performance' },
  { key: 'presentation',     label: 'Presentation & Group Activities' },
  { key: 'quarterly_tests',  label: 'Quarterly Tests' },
  { key: 'proactive_client', label: 'Proactive-Initiative & Client Satisfaction' },
];

const GROUPS = [
  { id: 'partners', title: 'Head Partners', subtitle: 'Firm leadership · Setting the tone at the top',
    match: (d) => /partner/i.test(d) && !/associate/i.test(d) },
  { id: 'associates', title: 'Associates', subtitle: 'Qualified professionals leading engagements',
    match: (d) => /^associate$/i.test(d) || /associate ca/i.test(d) },
  { id: 'managers', title: 'Managers', subtitle: 'Engagement and practice management',
    match: (d) => /manager/i.test(d) },
  { id: 'sr-articles', title: 'Senior Articled Assistants', subtitle: 'CA finalists driving audit, tax and assurance work',
    match: (d) => /senior\s+articled?\s+assistant/i.test(d) },
  { id: 'jr-articles', title: 'Junior Articled Assistants', subtitle: 'Emerging chartered accountants in training',
    match: (d) => /junior\s+articled?\s+assistant/i.test(d) },
  { id: 'sr-accountants', title: 'Senior Accountants', subtitle: 'Books, reporting and compliance backbone',
    match: (d) => /senior\s+accountant/i.test(d) || /senior\s+audit\s+executive/i.test(d) },
  { id: 'sr-executives', title: 'Senior Executives', subtitle: 'Client delivery and compliance execution',
    match: (d) => (/executive/i.test(d) && !/junior/i.test(d) && !/trainee/i.test(d)) &&
                  !/senior\s+audit\s+executive/i.test(d) },
  { id: 'jr-executives', title: 'Junior Executives', subtitle: 'Building foundational compliance skills',
    match: (d) => /junior\s+accountant/i.test(d) || (/junior/i.test(d) && /executive/i.test(d)) || /trainee/i.test(d) },
  { id: 'support', title: 'Office Assistant Staff', subtitle: 'The backbone of everyday operations',
    match: (d) => /attender|helper|receptionist|office\s+assistant/i.test(d) },
];

const bucketize = (list) => {
  const groups = GROUPS.map((g) => ({ ...g, members: [] }));
  const unmatched = [];
  for (const emp of list) {
    let placed = false;
    for (const g of groups) {
      if (g.match(emp.designation || '')) { g.members.push(emp); placed = true; break; }
    }
    if (!placed) unmatched.push(emp);
  }
  return { groups, unmatched };
};

const WORK_TONE = {
  'Excellent': 'bg-green-100 text-green-800 border-green-200',
  'Very Good': 'bg-blue-100 text-blue-800 border-blue-200',
  'Good': 'bg-amber-100 text-amber-800 border-amber-200',
  'Average': 'bg-gray-100 text-gray-700 border-gray-200',
};

const initials = (name) =>
  (name || '').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

// Primary / default firm entity. Members with this (or no) entity stay in the
// main block; everyone else is split into their own labelled sub-section.
const PRIMARY_ENTITY = 'RST & Co';
const normEntity = (v) => {
  const s = (v || '').trim();
  if (!s) return PRIMARY_ENTITY;
  if (/^rst\s*&?\s*co\.?$/i.test(s)) return PRIMARY_ENTITY;
  return s;
};

// Split an (already seniority-sorted) member list into entity sub-buckets.
// Primary entity first, then the rest alphabetically. Order within each
// sub-bucket is preserved from the incoming list.
const subdivideByEntity = (members) => {
  const map = new Map();
  for (const emp of members) {
    const key = normEntity(emp.entity);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(emp);
  }
  const keys = Array.from(map.keys()).sort((a, b) => {
    if (a === PRIMARY_ENTITY) return -1;
    if (b === PRIMARY_ENTITY) return 1;
    return a.localeCompare(b);
  });
  return keys.map((k) => ({ entity: k, members: map.get(k) }));
};

// Convert between "DD-MM-YYYY" (display / storage) and "YYYY-MM-DD" (native date input value)
const toISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return '';
};
const fromISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
};

const emptyEmployee = () => ({
  name: '', designation: '', entity: '', date_of_joining: '', date_of_birth: '', date_of_leaving: '',
  reports_to_partner_id: '', work_profile: 'Excellent',
  qualification: '', contact: '', alt_contact: '', email: '', address: '', permanent_address: '',
  bank_name: '', account_number: '', branch_name: '', ifsc: '',
  specialization: [],
  assignments: [], ratings: {}, photo_path: null, resume_path: null, documents: {}, sort_order: 100, published: true,
});

const FIELD_LABELS = {
  name: 'Name',
  designation: 'Designation',
  entity: 'Entity / Sub-division',
  date_of_joining: 'Date of Joining',
  date_of_birth: 'Date of Birth',
  date_of_leaving: 'Date of Leaving',
  qualification: 'Qualification',
  contact: 'Contact',
  alt_contact: 'Alternate Mobile',
  email: 'Office Allotted Mail ID',
  address: 'Current Address',
  permanent_address: 'Permanent Address',
  work_profile: 'Work Profile',
  reports_to_partner_id: 'Reports to (Partner)',
  specialization: 'Specialization',
  assignments: 'Assignments',
  documents: 'Documents',
  photo_path: 'Photo',
};

const formatError = (detail) => {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => {
      const loc = Array.isArray(e?.loc) ? e.loc.filter((p) => p !== 'body') : [];
      const field = loc[0];
      const label = FIELD_LABELS[field] || (field ? String(field) : null);
      const msg = e?.msg || JSON.stringify(e);
      return label ? `${label}: ${msg}` : msg;
    }).join(' · ');
  }
  return String(detail);
};

const TeamPage = () => {
  const { user, ready, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('current'); // 'current' | 'past'
  const [q, setQ] = useState('');

  // Editor state
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assignInput, setAssignInput] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (ready && !user) navigate('/admin/login?next=/team', { replace: true });
  }, [ready, user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/employees?limit=500`);
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (ready && user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user]);

  const openNew = () => { setEditing(emptyEmployee()); setAssignInput(''); setError(''); };
  const openEdit = (emp) => {
    let spec = emp.specialization;
    if (typeof spec === 'string') spec = spec ? [spec] : [];
    if (!Array.isArray(spec)) spec = [];
    setEditing({ ...emp, specialization: spec, assignments: emp.assignments || [], ratings: emp.ratings || {} });
    setAssignInput(''); setError('');
  };
  const cancel = () => { setEditing(null); setError(''); };
  const handleField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const addAssignment = () => {
    const v = assignInput.trim(); if (!v) return;
    setEditing((e) => ({ ...e, assignments: [...(e.assignments || []), v] }));
    setAssignInput('');
  };
  const removeAssignment = (i) =>
    setEditing((e) => ({ ...e, assignments: e.assignments.filter((_, idx) => idx !== i) }));

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/admin/employees/photo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing((e) => ({ ...e, photo_path: data.path }));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Photo upload failed');
    } finally { setPhotoUploading(false); }
  };

  const save = async () => {
    if (!editing.name || !editing.designation) {
      setError('Name and Designation are required'); return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        name: editing.name, designation: editing.designation,
        entity: (editing.entity || '').trim() || null,
        date_of_joining: editing.date_of_joining,
        date_of_birth: editing.date_of_birth || null,
        date_of_leaving: editing.date_of_leaving || null,
        reports_to_partner_id: editing.reports_to_partner_id || null,
        work_profile: editing.work_profile || null,
        qualification: editing.qualification, contact: editing.contact,
        alt_contact: (editing.alt_contact || '').trim() || null,
        email: (editing.email || '').trim() || null,
        address: editing.address,
        permanent_address: (editing.permanent_address || '').trim() || null,
        bank_name: (editing.bank_name || '').trim() || null,
        account_number: (editing.account_number || '').trim() || null,
        branch_name: (editing.branch_name || '').trim() || null,
        ifsc: (editing.ifsc || '').trim().toUpperCase() || null,
        specialization: (editing.specialization || []).filter(Boolean).slice(0, 3), assignments: editing.assignments || [],
        ratings: editing.ratings || {},
        photo_path: editing.photo_path || null,
        resume_path: editing.resume_path || null,
        documents: editing.documents || {},
        sort_order: Number(editing.sort_order) || 100,
        published: !!editing.published,
      };
      if (editing.id) await axios.put(`${API}/admin/employees/${editing.id}`, payload);
      else await axios.post(`${API}/admin/employees`, payload);
      setEditing(null);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (emp) => {
    if (!window.confirm(`Delete ${emp.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/employees/${emp.id}`);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    }
  };

  // Derive partners + split current/past employees
  const partners = useMemo(
    () => employees.filter(e => /partner/i.test(e.designation) && !/associate/i.test(e.designation) && !e.date_of_leaving),
    [employees]
  );

  // Distinct entities across all employees, sorted alphabetically. Used to
  // populate the datalist in the editor and the sub-division buckets in each
  // partner group.
  const entities = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => {
      const val = (e.entity || '').trim();
      if (val) set.add(val);
    });
    return Array.from(set).sort();
  }, [employees]);
  const currentEmployees = useMemo(
    () => employees.filter(e => !e.date_of_leaving),
    [employees]
  );
  const pastEmployees = useMemo(
    () => employees.filter(e => !!e.date_of_leaving).sort((a, b) => {
      const parse = (s) => {
        const m = String(s || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (!m) return Number.MAX_SAFE_INTEGER;
        return new Date(`${m[3]}-${m[2]}-${m[1]}`).getTime();
      };
      return parse(a.date_of_joining) - parse(b.date_of_joining);
    }),
    [employees]
  );

  // Group current team by Partner. Partners themselves get their own "Firm Leadership" bucket.
  // Sort helper: earlier date_of_joining first (senior at top). Undated rows go last.
  const bySeniority = (a, b) => {
    const parse = (s) => {
      const m = String(s || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (!m) return Number.MAX_SAFE_INTEGER;
      return new Date(`${m[3]}-${m[2]}-${m[1]}`).getTime();
    };
    return parse(a.date_of_joining) - parse(b.date_of_joining);
  };

  const isOfficeAssistant = (emp) => /office\s*assistant|attendant|receptionist/i.test(emp.designation || '');

  const currentByPartner = useMemo(() => {
    const buckets = partners.map(p => ({
      id: p.id,
      partner: p,
      title: `Team of ${p.name}`,
      members: [],
    }));
    const unassigned      = { id: '__unassigned',        partner: null, title: 'Unassigned to Partner',   members: [] };
    const officeAssistants = { id: '__office_assistants', partner: null, title: 'Office Assistants · Receptionists · Attendants', members: [] };
    const leadership      = { id: '__leadership',        partner: null, title: 'Firm Leadership · Head Partners', members: partners };

    for (const emp of currentEmployees) {
      // Partners themselves show only in Leadership block
      if (partners.find(p => p.id === emp.id)) continue;
      if (isOfficeAssistant(emp)) { officeAssistants.members.push(emp); continue; }
      const b = buckets.find(x => x.id === emp.reports_to_partner_id);
      if (b) b.members.push(emp);
      else unassigned.members.push(emp);
    }

    // Sort every non-leadership bucket by seniority (earliest join first)
    for (const b of buckets) b.members.sort(bySeniority);
    unassigned.members.sort(bySeniority);
    officeAssistants.members.sort(bySeniority);
    leadership.members = [...leadership.members].sort(bySeniority);

    return [
      leadership,
      ...buckets.filter(b => b.members.length > 0),
      ...(officeAssistants.members.length ? [officeAssistants] : []),
      ...(unassigned.members.length ? [unassigned] : []),
    ];
  }, [partners, currentEmployees]);

  // Free-text search across the whole roster. Matches name, designation,
  // entity, contact, alternate mobile, email and qualification (case-insensitive).
  const matchesQ = (emp) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    const hay = [
      emp.name, emp.designation, emp.entity, emp.qualification,
      emp.contact, emp.alt_contact, emp.email,
      ...(Array.isArray(emp.specialization) ? emp.specialization : []),
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(needle);
  };

  // Apply search to each partner bucket; drop empty groups (except when search
  // is empty and we want to keep the original ordering intact).
  const filteredByPartner = useMemo(() => {
    if (!q.trim()) return currentByPartner;
    return currentByPartner
      .map((group) => ({ ...group, members: group.members.filter(matchesQ) }))
      .filter((group) => group.members.length > 0);
  }, [currentByPartner, q]);   // eslint-disable-line react-hooks/exhaustive-deps

  const filteredPast = useMemo(
    () => (q.trim() ? pastEmployees.filter(matchesQ) : pastEmployees),
    [pastEmployees, q]           // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { groups: currentRoleGroups, unmatched: currentUnmatched } = useMemo(
    () => bucketize(currentEmployees), [currentEmployees]
  );
  // eslint-disable-next-line no-unused-vars
  const _unusedRoleGroups = { currentRoleGroups, currentUnmatched };
  const total = employees.length;

  if (!ready || !user) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-4">
        <div className="text-center">
          <Lock className="text-brand-yellow mx-auto mb-4" size={32} />
          <p className="text-white/70 text-sm">Verifying access…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="team-page">
      {/* Admin control bar */}
      <div className="bg-[#0a0f1e] border-b border-white/10 text-white sticky top-0 z-30">
        <div className="max-w-[1720px] mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 flex items-center justify-center">
              <Lock size={14} className="text-brand-yellow" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-brand-yellow uppercase">
                Admin Dashboard
              </div>
              <div className="text-sm font-medium">Team Directory · {total} members</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={openNew}
              className="bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 rounded-none h-9 px-4 text-sm font-semibold"
              data-testid="admin-add-employee-btn"
            >
              <Plus size={16} className="mr-1.5" /> Add Employee
            </Button>
            <button
              onClick={() => navigate('/admin/cashflow')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Internal Cashflow register"
              data-testid="nav-cashflow-btn"
            >
              <span className="font-mono tracking-widest">₹</span> Cashflow
            </button>
            <button
              onClick={() => navigate('/admin/vehicles')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Office Vehicle Diary"
              data-testid="nav-vehicles-btn"
            >
              <span aria-hidden>🚗</span> Vehicles
            </button>
            <button
              onClick={() => navigate('/admin/payroll')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Payroll register"
              data-testid="nav-payroll-btn"
            >
              <span className="font-mono tracking-widest">₹</span> Payroll
            </button>
            <button
              onClick={() => navigate('/admin/insurances')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Insurance register"
              data-testid="nav-insurances-btn"
            >
              <span aria-hidden>🛡</span> Insurances
            </button>
            <button
              onClick={() => navigate('/admin/policies')}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Policies & SOP"
              data-testid="nav-policies-btn"
            >
              <span aria-hidden>📘</span> Policies
            </button>
            <button
              onClick={() => navigate('/admin/employees-list')}
              className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Flat list view"
              data-testid="admin-flat-list-btn"
            >
              <Settings size={13} /> Flat list
            </button>
            {isAdmin && (
              <button
                onClick={() => navigate('/admin/firm-documents')}
                className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
                title="Confidential Firm Documents"
                data-testid="nav-firm-docs-btn"
              >
                <span aria-hidden>📁</span> Firm Docs
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => navigate('/admin/access')}
                className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
                title="User & Access Control"
                data-testid="nav-access-btn"
              >
                <span aria-hidden>🔐</span> Access
              </button>
            )}
            <span className="hidden md:block text-xs text-white/50 border-l border-white/10 pl-3">
              {user.email}
            </span>
            <button
              onClick={async () => { await logout(); navigate('/'); }}
              className="inline-flex items-center gap-1.5 bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 text-xs font-semibold transition-colors px-3 py-2"
              data-testid="team-logout-btn"
              title="Sign out of admin panel"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative bg-[#0a0f1e] pt-16 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff18_1px,transparent_1px),linear-gradient(to_bottom,#ffffff18_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-yellow/15 blur-[160px] rounded-full pointer-events-none" />
        <div className="relative max-w-[1720px] mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-6">
                <Award size={14} className="text-brand-yellow" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
                  Internal · RST &amp; Co.
                </span>
              </div>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.02] tracking-tight mb-6">
                Team Directory.
              </h1>
              <p className="text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl">
                Partners, chartered accountants, and specialists — manage profiles, upload photos,
                and keep the internal roster up to date.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="border-l-2 border-brand-yellow pl-5 space-y-3">
                <div className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
                  At a glance
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-2 text-white/80">
                  <span className="text-sm">Total team</span>
                  <span className="font-heading text-3xl font-bold text-white">{total}</span>
                </div>
                <div className="flex justify-between items-baseline border-b border-white/10 pb-2 text-white/80">
                  <span className="text-sm">Partners</span>
                  <span className="font-heading text-3xl font-bold text-white">
                    {partners.length}
                  </span>
                </div>
                <div className="flex justify-between items-baseline text-white/80">
                  <span className="text-sm">Past employees</span>
                  <span className="font-heading text-3xl font-bold text-white">{pastEmployees.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tab switcher */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-20">
        <div className="max-w-[1720px] mx-auto px-4 flex items-center gap-1 overflow-x-auto py-3">
          <button
            onClick={() => setTab('current')}
            className={`px-5 py-2.5 text-sm font-medium border transition-colors whitespace-nowrap ${
              tab === 'current'
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-900'
            }`}
            data-testid="tab-current"
          >
            Current Team · {currentEmployees.length}
          </button>
          <button
            onClick={() => setTab('past')}
            className={`px-5 py-2.5 text-sm font-medium border transition-colors whitespace-nowrap ${
              tab === 'past'
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-900'
            }`}
            data-testid="tab-past"
          >
            Past Employees · {pastEmployees.length}
          </button>

          <div className="relative ml-auto min-w-[220px] md:min-w-[300px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${tab === 'current' ? 'current' : 'past'} team…`}
              className="w-full border border-gray-300 pl-9 pr-8 py-2 text-sm focus:outline-none focus:border-gray-900"
              data-testid="team-search"
            />
            {q && (
              <button
                onClick={() => setQ('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 p-0.5"
                title="Clear search"
                data-testid="team-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="max-w-[1720px] mx-auto px-4 py-24 flex justify-center">
          <Loader2 size={28} className="animate-spin text-gray-400" />
        </div>
      )}
      {error && !editing && !loading && (
        <div className="max-w-[1720px] mx-auto px-4 py-4">
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3">{error}</div>
        </div>
      )}

      {!loading && tab === 'current' && (
        <>
          {filteredByPartner.length === 0 && q.trim() && (
            <div className="max-w-[1720px] mx-auto px-4 py-16">
              <div className="text-center py-16 border border-dashed border-gray-300 bg-white text-gray-500 text-sm">
                No current team members match &ldquo;{q}&rdquo;.
              </div>
            </div>
          )}
          {filteredByPartner.map((group, gi) => {
            const isDark = gi === 0; // Leadership on dark
            return (
              <section
                key={group.id}
                className={`py-10 md:py-14 ${isDark ? 'bg-[#0a0f1e] text-white' : gi % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                data-testid={`partner-group-${group.id}`}
              >
                <div className="max-w-[1720px] mx-auto px-4">
                  <div className="mb-6">
                    <div className={`font-mono text-[11px] tracking-[0.25em] uppercase mb-3 ${
                      isDark ? 'text-brand-yellow' : 'text-brand-blue'
                    }`}>
                      {String(gi + 1).padStart(2, '0')} · {group.members.length} member{group.members.length === 1 ? '' : 's'}
                    </div>
                    <h2 className={`font-heading text-2xl md:text-3xl font-black tracking-tight leading-tight ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>{group.title}</h2>
                  </div>
                  {(() => {
                    const subs = subdivideByEntity(group.members);
                    return subs.map((sub) => (
                      <div key={sub.entity} className="mb-8 last:mb-0" data-testid={`entity-sub-${group.id}-${sub.entity.replace(/\s+/g, '-')}`}>
                        {subs.length > 1 && (
                          <div className="flex items-center gap-3 mb-3">
                            <span className={`font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 border ${
                              sub.entity === PRIMARY_ENTITY
                                ? (isDark ? 'border-white/25 text-white/80 bg-white/5' : 'border-gray-300 text-gray-700 bg-gray-100')
                                : (isDark ? 'border-brand-yellow/50 text-brand-yellow bg-brand-yellow/10' : 'border-brand-blue/40 text-brand-blue bg-brand-blue/5')
                            }`}>
                              {sub.entity}
                            </span>
                            <span className={`text-[11px] ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                              {sub.members.length} member{sub.members.length === 1 ? '' : 's'}
                            </span>
                            <div className={`flex-1 h-px ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} />
                          </div>
                        )}
                        <EmployeeTable
                          members={sub.members}
                          partners={partners}
                          isDark={isDark}
                          isPastView={false}
                          onEdit={(emp) => openEdit(emp)}
                          onDelete={(emp) => remove(emp)}
                          onEmployeeUpdated={load}
                        />
                      </div>
                    ));
                  })()}
                </div>
              </section>
            );
          })}
        </>
      )}

      {!loading && tab === 'past' && (
        <section className="py-10 md:py-14 bg-gray-50 min-h-[50vh]">
          <div className="max-w-[1720px] mx-auto px-4">
            <div className="mb-6">
              <div className="font-mono text-[11px] tracking-[0.25em] uppercase mb-3 text-brand-blue">
                Archive · {pastEmployees.length} record{pastEmployees.length === 1 ? '' : 's'}
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tight leading-tight text-gray-900">
                Past Employees
              </h2>
              <p className="text-sm mt-2 text-gray-600">
                Team members who have moved on from the firm. Set a &ldquo;Date of Leaving&rdquo; on any current
                employee to archive them here.
              </p>
            </div>
            {pastEmployees.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-gray-300 bg-white text-gray-500 text-sm">
                No past employees on record.
              </div>
            ) : filteredPast.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-gray-300 bg-white text-gray-500 text-sm">
                No past employees match &ldquo;{q}&rdquo;.
              </div>
            ) : (
              <EmployeeTable
                members={filteredPast}
                partners={partners}
                isDark={false}
                isPastView={true}
                onEdit={(emp) => openEdit(emp)}
                onDelete={(emp) => remove(emp)}
                onEmployeeUpdated={load}
              />
            )}
          </div>
        </section>
      )}

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm flex items-stretch justify-end">
          <div className="w-full max-w-5xl bg-white h-full overflow-y-auto shadow-2xl" data-testid="admin-employee-editor">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] text-gray-500 uppercase">
                  {editing.id ? 'Editing' : 'New'} Employee
                </div>
                <h2 className="font-heading text-xl font-bold text-gray-900">{editing.name || 'Untitled'}</h2>
              </div>
              <button onClick={cancel} className="p-2 text-gray-500 hover:text-gray-900" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-mono tracking-widest text-gray-600 uppercase mb-2">Photo</label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 border border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden">
                    {editing.photo_path ? (
                      <img src={`${API}/media/${editing.photo_path}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-gray-400 text-xs">No photo</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                    <Button type="button" onClick={() => fileRef.current?.click()} disabled={photoUploading}
                      className="rounded-none bg-gray-900 hover:bg-black text-white"
                      data-testid="admin-upload-photo-btn">
                      {photoUploading ? (
                        <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</>
                      ) : (
                        <><Upload size={14} className="mr-2" /> Upload / Replace</>
                      )}
                    </Button>
                    <p className="text-[11px] text-gray-500 mt-1">JPEG / PNG · up to 8 MB.</p>
                  </div>
                </div>
              </div>

              <FieldGroup label="Name*">
                <input value={editing.name} onChange={(e) => handleField('name', e.target.value)}
                  className="admin-input" data-testid="editor-name" />
              </FieldGroup>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Designation*">
                  <input value={editing.designation}
                    onChange={(e) => handleField('designation', e.target.value)}
                    list="employee-designations"
                    className="admin-input"
                    placeholder="Select or type…"
                    data-testid="editor-designation" />
                  <datalist id="employee-designations">
                    {DESIGNATIONS.map((d) => <option key={d} value={d} />)}
                    {editing.designation && !DESIGNATIONS.includes(editing.designation) && (
                      <option value={editing.designation} />
                    )}
                  </datalist>
                </FieldGroup>
                <FieldGroup label="Entity / Sub-division">
                  <input
                    value={editing.entity || ''}
                    onChange={(e) => handleField('entity', e.target.value)}
                    list="employee-entities"
                    className="admin-input"
                    placeholder="e.g. RST & Co., SCCS LLP — or type a new one"
                    data-testid="editor-entity"
                  />
                  <datalist id="employee-entities">
                    {entities.map((v) => <option key={v} value={v} />)}
                  </datalist>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {entities.length
                      ? `Pick from ${entities.length} existing entit${entities.length === 1 ? 'y' : 'ies'}, or type a new one to add it.`
                      : 'Type to create the first entity — it will be available for others.'}
                  </p>
                </FieldGroup>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Date of Joining">
                  <input
                    type="date"
                    value={toISODate(editing.date_of_joining)}
                    onChange={(e) => handleField('date_of_joining', fromISODate(e.target.value))}
                    className="admin-input"
                    data-testid="editor-date-joining"
                  />
                </FieldGroup>
                <FieldGroup label="Date of Birth">
                  <input
                    type="date"
                    value={toISODate(editing.date_of_birth || '')}
                    onChange={(e) => handleField('date_of_birth', fromISODate(e.target.value))}
                    className="admin-input"
                    data-testid="editor-date-birth"
                  />
                </FieldGroup>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Reports to (Partner)">
                  <select
                    value={editing.reports_to_partner_id || ''}
                    onChange={(e) => handleField('reports_to_partner_id', e.target.value)}
                    className="admin-input"
                    data-testid="editor-partner"
                  >
                    <option value="">— unassigned —</option>
                    {partners.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </FieldGroup>
                <FieldGroup label="Date of Leaving (if applicable)">
                  <input
                    type="date"
                    value={toISODate(editing.date_of_leaving || '')}
                    onChange={(e) => handleField('date_of_leaving', fromISODate(e.target.value))}
                    className="admin-input"
                    data-testid="editor-date-leaving"
                  />
                </FieldGroup>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Work Profile">
                  <select value={editing.work_profile || ''} onChange={(e) => handleField('work_profile', e.target.value)} className="admin-input">
                    <option value="">— select —</option>
                    {WORK_PROFILES.map((w) => (<option key={w} value={w}>{w}</option>))}
                  </select>
                </FieldGroup>
                <FieldGroup label="Sort Order">
                  <input
                    type="number"
                    value={editing.sort_order ?? 100}
                    onChange={(e) => handleField('sort_order', e.target.value)}
                    className="admin-input"
                    data-testid="editor-sort-order"
                  />
                </FieldGroup>
              </div>

              <FieldGroup label="Specialization (up to 3)">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {[0, 1, 2].map((i) => {
                      const list = editing.specialization || [];
                      const value = list[i] || '';
                      const setAt = (v) => {
                        const next = [...list];
                        while (next.length < 3) next.push('');
                        next[i] = v;
                        handleField('specialization', next.filter(Boolean));
                      };
                      const otherPicks = list.filter((_, idx) => idx !== i);
                      return (
                        <select
                          key={i}
                          value={value}
                          onChange={(e) => setAt(e.target.value)}
                          className="admin-input"
                          data-testid={`editor-specialization-${i + 1}`}
                        >
                          <option value="">— {i === 0 ? 'primary' : i === 1 ? 'secondary' : 'tertiary'} —</option>
                          {SPECIALIZATIONS.filter((s) => !otherPicks.includes(s)).map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                          {value && !SPECIALIZATIONS.includes(value) && (
                            <option value={value}>{value} (legacy)</option>
                          )}
                        </select>
                      );
                    })}
                  </div>
                </FieldGroup>

              <FieldGroup label="Qualification">
                <input value={editing.qualification} onChange={(e) => handleField('qualification', e.target.value)}
                  placeholder="FCA, CS, DISA" className="admin-input" />
              </FieldGroup>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FieldGroup label="Contact*">
                  <input
                    value={editing.contact}
                    onChange={(e) => handleField('contact', e.target.value)}
                    placeholder="+91 99646 66007"
                    className="admin-input"
                    data-testid="editor-contact"
                  />
                </FieldGroup>
                <FieldGroup label="Alternate Mobile">
                  <input
                    value={editing.alt_contact || ''}
                    onChange={(e) => handleField('alt_contact', e.target.value)}
                    placeholder="Secondary / emergency number"
                    className="admin-input"
                    data-testid="editor-alt-contact"
                  />
                </FieldGroup>
                <FieldGroup label="Office Allotted Mail ID">
                  <input
                    type="email"
                    value={editing.email || ''}
                    onChange={(e) => handleField('email', e.target.value)}
                    placeholder="employee@rstandco.co.in"
                    className="admin-input"
                    data-testid="editor-email"
                  />
                </FieldGroup>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Current Address*">
                  <textarea
                    value={editing.address}
                    onChange={(e) => handleField('address', e.target.value)}
                    rows={2}
                    className="admin-input"
                    placeholder="Where the employee currently lives"
                    data-testid="editor-current-address"
                  />
                </FieldGroup>
                <FieldGroup label="Permanent Address">
                  <textarea
                    value={editing.permanent_address || ''}
                    onChange={(e) => handleField('permanent_address', e.target.value)}
                    rows={2}
                    className="admin-input"
                    placeholder="Native / permanent address (as per Aadhar)"
                    data-testid="editor-permanent-address"
                  />
                </FieldGroup>
              </div>

              {/* Bank details (permanent) */}
              <div className="border border-gray-200 p-4">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-3">
                  Bank Details <span className="text-gray-400 normal-case tracking-normal">· for salary disbursement</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FieldGroup label="Name of Bank">
                    <input
                      value={editing.bank_name || ''}
                      onChange={(e) => handleField('bank_name', e.target.value)}
                      className="admin-input"
                      placeholder="e.g. HDFC Bank"
                      data-testid="editor-bank-name"
                    />
                  </FieldGroup>
                  <FieldGroup label="Account Number">
                    <input
                      value={editing.account_number || ''}
                      onChange={(e) => handleField('account_number', e.target.value)}
                      className="admin-input"
                      placeholder="Account no."
                      data-testid="editor-account-number"
                    />
                  </FieldGroup>
                  <FieldGroup label="Branch Name">
                    <input
                      value={editing.branch_name || ''}
                      onChange={(e) => handleField('branch_name', e.target.value)}
                      className="admin-input"
                      placeholder="e.g. Rajajinagar"
                      data-testid="editor-branch-name"
                    />
                  </FieldGroup>
                  <FieldGroup label="IFSC No.">
                    <input
                      value={editing.ifsc || ''}
                      onChange={(e) => handleField('ifsc', e.target.value.toUpperCase())}
                      className="admin-input uppercase"
                      placeholder="e.g. HDFC0001234"
                      data-testid="editor-ifsc"
                    />
                  </FieldGroup>
                </div>
                {editing.id && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase mb-2">
                      Bank Proof · Cheque Leaf / Passbook First Page
                    </div>
                    <BankProofUpload emp={editing} onUploaded={(path) =>
                      setEditing((e) => ({ ...e, documents: { ...(e.documents || {}), bank_proof: path } }))
                    } />
                  </div>
                )}
                {!editing.id && (
                  <p className="text-[11px] text-gray-400 mt-3">Save the employee first, then re-open to upload the cheque leaf / passbook page.</p>
                )}
                {editing.id && <PayslipSection employeeId={editing.id} />}
              </div>

              <FieldGroup label="Assignments Handled">
                <div className="flex gap-2 mb-2">
                  <input value={assignInput} onChange={(e) => setAssignInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAssignment(); } }}
                    placeholder="e.g. Statutory Audit" className="admin-input flex-1" />
                  <Button type="button" onClick={addAssignment} className="rounded-none bg-gray-900 hover:bg-black text-white px-4">
                    Add
                  </Button>
                </div>
                {(editing.assignments || []).length > 0 && (
                  <ul className="space-y-1">
                    {editing.assignments.map((a, i) => (
                      <li key={i} className="flex items-center justify-between text-sm bg-gray-50 border border-gray-200 px-3 py-1.5">
                        <span>{a}</span>
                        <button onClick={() => removeAssignment(i)} className="text-gray-400 hover:text-red-600"><X size={14} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </FieldGroup>

              <FieldGroup label="Performance Rating (0 – 5 stars · half-star supported)">
                <div className="space-y-2.5 bg-amber-50/40 border border-amber-100 p-4">
                  {RATING_CATEGORIES.map((cat, idx) => {
                    const val = Number((editing.ratings || {})[cat.key] || 0);
                    const setVal = (v) => {
                      const next = { ...(editing.ratings || {}) };
                      if (!v) delete next[cat.key];
                      else next[cat.key] = v;
                      handleField('ratings', next);
                    };
                    return (
                      <div key={cat.key} className="flex items-center justify-between gap-4">
                        <div className="text-xs md:text-sm text-gray-700 min-w-0 flex-1">
                          <span className="font-mono text-[10px] text-gray-400 mr-2">{String(idx + 1).padStart(2, '0')}</span>
                          {cat.label}
                        </div>
                        <StarRating
                          value={val}
                          onChange={setVal}
                          testIdPrefix={`rating-${cat.key}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </FieldGroup>

              {editing.id && (
                <FieldGroup label="Ratings Timeline (per quarter)">
                  <RatingsTimeline
                    employeeId={editing.id}
                    currentRatings={editing.ratings || {}}
                    onCurrentUpdated={load}
                  />
                </FieldGroup>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button onClick={() => handleField('published', !editing.published)} type="button"
                  className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-medium border ${
                    editing.published ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-600'
                  }`}>
                  {editing.published ? <Eye size={14} /> : <EyeOff size={14} />}
                  {editing.published ? 'Published' : 'Draft'}
                </button>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{error}</div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <Button onClick={cancel} variant="outline" className="rounded-none bg-white hover:bg-gray-100 text-gray-700 border border-gray-300">Cancel</Button>
              <Button onClick={save} disabled={saving} className="rounded-none bg-brand-blue hover:bg-brand-blue-hover text-white"
                data-testid="admin-save-employee-btn">
                {saving ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</>) : (<><Save size={14} className="mr-2" /> Save</>)}
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-input { width: 100%; border: 1px solid #d1d5db; padding: 10px 12px; font-size: 14px; outline: none; background: white; }
        .admin-input:focus { border-color: #111827; }
      `}</style>
    </div>
  );
};

const avgRating = (r) => {
  if (!r) return 0;
  const vals = RATING_CATEGORIES.map((c) => Number(r[c.key] || 0)).filter((v) => v > 0);
  if (!vals.length) return 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};

// Returns { isThisMonth, day, monthName } for a DD-MM-YYYY string, or null.
const parseDob = (s) => {
  if (!s) return null;
  const m = String(s).match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (!month || month < 1 || month > 12) return null;
  const now = new Date();
  const monthName = new Date(2000, month - 1, 1).toLocaleString('en-IN', { month: 'long' });
  return {
    day,
    month,
    monthName,
    isThisMonth: month === now.getMonth() + 1,
    isToday: month === now.getMonth() + 1 && day === now.getDate(),
  };
};

const EmployeeTable = ({ members, partners, isDark, onEdit, onDelete, onEmployeeUpdated, isPastView = false }) => {
  const [zoomed, setZoomed] = useState(null); // { photoUrl, name, designation }

  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e) => { if (e.key === 'Escape') setZoomed(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomed]);

  if (!members || members.length === 0) {
    return (
      <div className={`text-center py-10 border border-dashed text-sm ${
        isDark ? 'border-white/15 text-white/50 bg-white/[0.02]' : 'border-gray-300 text-gray-500 bg-white'
      }`}>
        No members in this group.
      </div>
    );
  }

  const headBg = isDark ? 'bg-[#111827] text-white/70 border-b border-white/10' : 'bg-gray-900 text-white';
  const rowBg = isDark ? 'hover:bg-white/[0.04] border-b border-white/10' : 'hover:bg-gray-50 border-b border-gray-100';
  const rowText = isDark ? 'text-white/85' : 'text-gray-700';
  const muted = isDark ? 'text-white/50' : 'text-gray-500';

  return (
    <div className={`border ${isDark ? 'border-white/10 bg-[#0a0f1e]' : 'border-gray-200 bg-white'}`}>
      <table className="w-full text-xs md:text-sm table-fixed">
        <thead className={`text-[10px] font-mono tracking-widest uppercase sticky top-[128px] z-10 shadow-[0_2px_4px_-1px_rgba(0,0,0,0.35)] ${headBg}`}>
          <tr>
            <th className="text-left px-2 py-3 w-9">#</th>
            <th className="text-left px-2 py-3" style={{ width: '20%' }}>Employee</th>
            <th className="text-left px-2 py-3 w-24 whitespace-nowrap">Joined</th>
            <th className="text-left px-2 py-3 w-24 whitespace-nowrap">DOB</th>
            {isPastView && (
              <th className="text-left px-2 py-3 w-24 whitespace-nowrap">Left</th>
            )}
            <th className="text-left px-2 py-3" style={{ width: '16%' }}>Specialization</th>
            <th className="text-center px-2 py-3 w-24">Rating</th>
            <th className="text-left px-2 py-3 w-28">Contact</th>
            <th className="text-left px-2 py-3" style={{ width: '15%' }}>Address</th>
            <th className="text-left px-2 py-3" style={{ width: '12%' }}>Assignments</th>
            <th className="text-center px-2 py-3 w-28">Documents</th>
            <th className="text-right px-2 py-3 w-16">Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map((emp, i) => {
            const photoUrl = emp.photo_path ? `${API}/media/${emp.photo_path}` : null;
            const specs = Array.isArray(emp.specialization) ? emp.specialization.filter(Boolean) : (emp.specialization ? [emp.specialization] : []);
            const avg = avgRating(emp.ratings);
            return (
              <tr key={emp.id} className={`${rowBg} ${rowText} group align-top`} data-testid={`employee-row-${emp.id}`}>
                <td className={`px-2 py-3 font-mono text-[11px] tabular-nums ${muted}`}>{i + 1}</td>
                {/* Employee: photo + name + designation */}
                <td className="px-2 py-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-[88px] h-[88px] flex-shrink-0 flex items-center justify-center overflow-hidden ${
                        isDark ? 'bg-white/10' : 'bg-brand-blue/10'
                      } ${photoUrl ? 'cursor-zoom-in ring-transparent hover:ring-2 hover:ring-brand-yellow transition-all' : ''}`}
                      onClick={photoUrl ? () => setZoomed({ photoUrl, name: emp.name, designation: emp.designation }) : undefined}
                      role={photoUrl ? 'button' : undefined}
                      tabIndex={photoUrl ? 0 : undefined}
                      onKeyDown={photoUrl ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setZoomed({ photoUrl, name: emp.name, designation: emp.designation }); }
                      } : undefined}
                      data-testid={`photo-${emp.id}`}
                      title={photoUrl ? `View ${emp.name}'s photo` : undefined}
                    >
                      {photoUrl ? (
                        <img src={photoUrl} alt={emp.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      ) : (
                        <span className={`font-heading font-bold text-2xl ${isDark ? 'text-brand-yellow' : 'text-brand-blue'}`}>
                          {initials(emp.name)}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className={`font-heading font-bold text-sm leading-tight ${isDark ? 'text-white' : 'text-gray-900'} inline-flex items-center gap-1.5`}>
                        <span>{emp.name}</span>
                        {(() => {
                          const dob = parseDob(emp.date_of_birth);
                          if (!dob || !dob.isThisMonth) return null;
                          return (
                            <span
                              className={`inline-flex items-center justify-center w-5 h-5 ${
                                dob.isToday
                                  ? 'bg-pink-500 text-white animate-pulse'
                                  : 'bg-pink-100 text-pink-700 border border-pink-200'
                              }`}
                              title={dob.isToday ? `🎉 Birthday today! (${emp.date_of_birth})` : `Birthday this month · ${dob.day} ${dob.monthName}`}
                              data-testid={`birthday-${emp.id}`}
                            >
                              <Cake size={11} />
                            </span>
                          );
                        })()}
                      </div>
                      <div className={`text-[11px] ${isDark ? 'text-brand-yellow' : 'text-brand-blue'} font-medium`}>
                        {emp.designation}
                        {emp.entity && (
                          <span className={isDark ? 'text-white/60' : 'text-gray-500'}>
                            {' — '}{emp.entity}
                          </span>
                        )}
                      </div>
                      {emp.qualification && (
                        <div className={`text-[11px] leading-snug mt-0.5 ${isDark ? 'text-white/60' : 'text-gray-500'}`} title={emp.qualification}>
                          {emp.qualification}
                        </div>
                      )}
                      {!emp.published && (
                        <span className={`inline-block mt-1 text-[9px] font-mono tracking-widest uppercase px-1.5 py-0.5 border ${
                          isDark ? 'bg-white/5 text-white/60 border-white/10' : 'bg-gray-100 text-gray-600 border-gray-200'
                        }`}>Draft</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className={`px-2 py-3 font-mono text-[11px] tabular-nums whitespace-nowrap`}>{emp.date_of_joining || <span className={muted}>—</span>}</td>
                <td className={`px-2 py-3 font-mono text-[11px] tabular-nums whitespace-nowrap`}>
                  {(() => {
                    if (!emp.date_of_birth) return <span className={muted}>—</span>;
                    const dob = parseDob(emp.date_of_birth);
                    if (dob && dob.isThisMonth) {
                      return (
                        <span className={`inline-flex items-center gap-1 font-semibold ${isDark ? 'text-pink-300' : 'text-pink-700'}`}
                              title={dob.isToday ? '🎉 Birthday today!' : `Birthday this month · ${dob.day} ${dob.monthName}`}>
                          {emp.date_of_birth}
                        </span>
                      );
                    }
                    return emp.date_of_birth;
                  })()}
                </td>
                {isPastView && (
                  <td className={`px-2 py-3 font-mono text-[11px] tabular-nums whitespace-nowrap`}>
                    {emp.date_of_leaving ? <span className={isDark ? 'text-red-300' : 'text-red-600'}>{emp.date_of_leaving}</span> : <span className={muted}>—</span>}
                  </td>
                )}
                <td className="px-2 py-3">
                  {specs.length ? (
                    <div className="flex flex-wrap gap-1">
                      {specs.map((s, idx) => (
                        <span key={idx} className={`text-[10px] px-1.5 py-0.5 border leading-tight ${
                          isDark ? 'bg-white/5 text-white/70 border-white/10' : 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}>{s}</span>
                      ))}
                    </div>
                  ) : <span className={muted}>—</span>}
                </td>
                <td className="px-2 py-3 text-center">
                  {avg > 0 ? (
                    <div className="inline-flex flex-col items-center">
                      <StarRow value={avg} isDark={isDark} size={11} />
                      <span className={`text-[10px] font-mono mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>{avg.toFixed(1)} / 5</span>
                    </div>
                  ) : <span className={muted}>—</span>}
                </td>
                <td className="px-2 py-3">
                  {emp.contact ? (
                    <a href={`tel:${emp.contact.replace(/\s+/g, '')}`}
                      className={`inline-flex items-center gap-1.5 hover:underline ${
                        isDark ? 'text-white/85 hover:text-brand-yellow' : 'text-gray-700 hover:text-brand-blue'
                      }`}>
                      <Phone size={11} className={isDark ? 'text-brand-yellow' : 'text-brand-blue'} />
                      <span className="tabular-nums text-[11px]">{emp.contact}</span>
                    </a>
                  ) : <span className={muted}>—</span>}
                </td>
                <td className="px-2 py-3">
                  {emp.address ? (
                    <div className={`flex items-start gap-1.5 text-[11px] leading-snug ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                      <MapPin size={10} className="mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{emp.address}</span>
                    </div>
                  ) : <span className={muted}>—</span>}
                </td>
                <td className="px-2 py-3">
                  {(emp.assignments || []).length ? (
                    <div className="flex flex-wrap gap-1">
                      {(emp.assignments || []).slice(0, 4).map((a, idx) => (
                        <span key={idx} className={`text-[10px] px-1.5 py-0.5 border leading-tight ${
                          isDark ? 'bg-white/5 text-white/70 border-white/10' : 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}>{a}</span>
                      ))}
                      {(emp.assignments || []).length > 4 && (
                        <span className={`text-[10px] px-1.5 py-0.5 ${muted}`}>+{emp.assignments.length - 4}</span>
                      )}
                    </div>
                  ) : <span className={muted}>—</span>}
                </td>
                <td className="px-2 py-2">
                  <DocumentsCell emp={emp} isDark={isDark} onUpdated={onEmployeeUpdated} />
                </td>
                <td className="px-2 py-3 text-right">
                  <div className="inline-flex gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => onEdit(emp)} title="Edit"
                      className={`p-1.5 border ${isDark ? 'bg-white/5 border-white/20 text-white hover:bg-brand-yellow hover:text-gray-900' : 'bg-white border-gray-300 text-gray-600 hover:border-brand-blue hover:text-brand-blue'}`}
                      data-testid={`edit-${emp.id}`}>
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => onDelete(emp)} title="Delete"
                      className={`p-1.5 border ${isDark ? 'bg-white/5 border-white/20 text-white hover:bg-red-600 hover:border-red-600' : 'bg-white border-gray-300 text-gray-600 hover:border-red-500 hover:text-red-600'}`}
                      data-testid={`delete-${emp.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {zoomed && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setZoomed(null)}
          role="dialog"
          aria-modal="true"
          data-testid="photo-zoom-overlay"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setZoomed(null); }}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-brand-yellow hover:text-gray-900 text-white flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="photo-zoom-close"
          >
            <X size={22} />
          </button>
          <figure
            className="relative max-w-[90vw] max-h-[88vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={zoomed.photoUrl}
              alt={zoomed.name}
              className="max-w-full max-h-[80vh] object-contain shadow-2xl"
              data-testid="photo-zoom-img"
            />
            <figcaption className="mt-4 text-center">
              <div className="font-heading text-xl md:text-2xl font-bold text-white">{zoomed.name}</div>
              {zoomed.designation && (
                <div className="text-brand-yellow text-sm font-mono tracking-widest uppercase mt-1">{zoomed.designation}</div>
              )}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
};

const DOC_KINDS = [
  { key: 'resume',       label: 'Resume',       short: 'R' },
  { key: 'offer_letter', label: 'Offer Letter', short: 'O' },
  { key: 'e_contract',   label: 'E-Contract',   short: 'E' },
  { key: 'aadhar_kyc',   label: 'Aadhar KYC',   short: 'A' },
  { key: 'bank_proof',   label: 'Bank Proof',   short: 'B' },
];

// Upload the cheque leaf / passbook page from inside the employee editor.
// Uploads to the shared resume endpoint with kind=bank_proof, then hands the
// stored path back so it is saved with the employee on Save.
const BankProofUpload = ({ emp, onUploaded }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef(null);
  const path = emp.documents?.bank_proof;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', 'bank_proof');
      const { data } = await axios.post(`${API}/admin/employees/resume`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(data.path);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {path && (
        <a href={`${API}/media/${path}`} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline"
          data-testid="bank-proof-link">
          <FileText size={14} /> View current proof
        </a>
      )}
      <input ref={ref} type="file" accept="image/*,.pdf" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} />
      <Button type="button" onClick={() => ref.current?.click()} disabled={busy}
        className="rounded-none bg-gray-900 hover:bg-black text-white h-9"
        data-testid="editor-bank-proof-upload">
        {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</>
              : <><Upload size={14} className="mr-2" /> {path ? 'Replace' : 'Upload'} cheque / passbook</>}
      </Button>
      <span className="text-[11px] text-gray-400">Image or PDF · up to 10 MB</span>
      {err && <span className="text-[11px] text-red-600">{err}</span>}
    </div>
  );
};

const DocumentsCell = ({ emp, isDark, onUpdated }) => {
  const [busyKind, setBusyKind] = useState(null);
  const inputRefs = useRef({});
  // Merge legacy resume_path into the docs map for display
  const docs = { ...(emp.documents || {}) };
  if (!docs.resume && emp.resume_path) docs.resume = emp.resume_path;

  const upload = async (kind, file) => {
    if (!file) return;
    setBusyKind(kind);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('kind', kind);
      const { data } = await axios.post(`${API}/admin/employees/resume`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const nextDocs = { ...docs, [kind]: data.path };
      const payload = {
        name: emp.name,
        designation: emp.designation || '',
        entity: emp.entity || null,
        date_of_joining: emp.date_of_joining || null,
        date_of_birth: emp.date_of_birth || null,
        date_of_leaving: emp.date_of_leaving || null,
        reports_to_partner_id: emp.reports_to_partner_id || null,
        work_profile: emp.work_profile || 'Excellent',
        qualification: emp.qualification || '',
        contact: emp.contact || '',
        alt_contact: emp.alt_contact || null,
        email: emp.email || null,
        address: emp.address || '',
        permanent_address: emp.permanent_address || null,
        bank_name: emp.bank_name || null,
        account_number: emp.account_number || null,
        branch_name: emp.branch_name || null,
        ifsc: emp.ifsc || null,
        specialization: emp.specialization || [],
        assignments: emp.assignments || [],
        ratings: emp.ratings || {},
        photo_path: emp.photo_path || null,
        resume_path: kind === 'resume' ? data.path : (emp.resume_path || null),
        documents: nextDocs,
        sort_order: emp.sort_order || 100,
        published: emp.published !== false,
      };
      await axios.put(`${API}/admin/employees/${emp.id}`, payload);
      await onUpdated?.();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Document upload failed');
    } finally {
      setBusyKind(null);
      if (inputRefs.current[kind]) inputRefs.current[kind].value = '';
    }
  };

  const btnBase = isDark
    ? 'border-white/20 text-white/85 hover:bg-brand-yellow hover:text-gray-900'
    : 'border-gray-300 text-gray-600 hover:border-brand-blue hover:text-brand-blue';

  return (
    <div className="flex flex-col gap-1">
      {DOC_KINDS.map(({ key, label, short }) => {
        const has = !!docs[key];
        const isBusy = busyKind === key;
        return (
          <div key={key} className="flex items-center gap-1.5" data-testid={`doc-row-${key}-${emp.id}`}>
            <span
              className={`inline-flex items-center justify-center w-5 h-5 border font-mono text-[10px] font-bold ${
                has
                  ? (isDark ? 'bg-brand-yellow text-gray-900 border-brand-yellow' : 'bg-brand-blue text-white border-brand-blue')
                  : (isDark ? 'border-white/15 text-white/40' : 'border-gray-200 text-gray-400')
              }`}
              title={label}
            >
              {short}
            </span>
            <input
              ref={(el) => { inputRefs.current[key] = el; }}
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => upload(key, e.target.files?.[0])}
              data-testid={`doc-input-${key}-${emp.id}`}
            />
            {has && (
              <a
                href={`${API}/media/${docs[key]}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`p-0.5 border ${btnBase}`}
                title={`View ${label}`}
                data-testid={`doc-view-${key}-${emp.id}`}
              >
                <FileText size={10} />
              </a>
            )}
            <button
              onClick={() => inputRefs.current[key]?.click()}
              disabled={isBusy}
              className={`p-0.5 border ${btnBase}`}
              title={has ? `Replace ${label}` : `Upload ${label} (PDF / Word)`}
              data-testid={`doc-upload-${key}-${emp.id}`}
            >
              {isBusy ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
            </button>
          </div>
        );
      })}
    </div>
  );
};

const FieldGroup = ({ label, children }) => (
  <div>
    <label className="block text-xs font-mono tracking-widest text-gray-600 uppercase mb-2">{label}</label>
    {children}
  </div>
);

// Editable half-star row. Click left half of a star = .5, right half = full. Click same value to clear.
const StarRating = ({ value = 0, onChange, size = 22, testIdPrefix }) => {
  const [hover, setHover] = useState(null);
  const display = hover != null ? hover : value;
  return (
    <div className="inline-flex items-center gap-0.5" onMouseLeave={() => setHover(null)}>
      {[1, 2, 3, 4, 5].map((i) => {
        const full = display >= i;
        const half = !full && display >= i - 0.5;
        return (
          <span
            key={i}
            className="relative inline-block cursor-pointer select-none"
            style={{ width: size, height: size }}
            data-testid={testIdPrefix ? `${testIdPrefix}-star-${i}` : undefined}
          >
            {/* base outline */}
            <Star size={size} className="text-gray-300 absolute inset-0" strokeWidth={1.5} />
            {/* fill overlay */}
            {(full || half) && (
              <span className="absolute inset-0 overflow-hidden" style={{ width: full ? '100%' : '50%' }}>
                <Star size={size} className="text-amber-400 fill-amber-400" strokeWidth={1.5} />
              </span>
            )}
            {/* left half hit zone */}
            <button
              type="button"
              aria-label={`${i - 0.5} stars`}
              onMouseEnter={() => setHover(i - 0.5)}
              onClick={() => onChange?.(value === i - 0.5 ? 0 : i - 0.5)}
              className="absolute top-0 left-0 h-full w-1/2"
              style={{ background: 'transparent', border: 0, padding: 0 }}
            />
            {/* right half hit zone */}
            <button
              type="button"
              aria-label={`${i} stars`}
              onMouseEnter={() => setHover(i)}
              onClick={() => onChange?.(value === i ? 0 : i)}
              className="absolute top-0 right-0 h-full w-1/2"
              style={{ background: 'transparent', border: 0, padding: 0 }}
            />
          </span>
        );
      })}
      <span className="ml-2 text-xs font-mono text-gray-500 tabular-nums w-8">
        {display ? display.toFixed(1) : '—'}
      </span>
    </div>
  );
};

// Compact read-only star row for cards.
const StarRow = ({ value = 0, size = 12, isDark = false }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => {
      const full = value >= i;
      const half = !full && value >= i - 0.5;
      return (
        <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
          <Star size={size} className={isDark ? 'text-white/20 absolute inset-0' : 'text-gray-300 absolute inset-0'} strokeWidth={1.5} />
          {(full || half) && (
            <span className="absolute inset-0 overflow-hidden" style={{ width: full ? '100%' : '50%' }}>
              <Star size={size} className="text-amber-400 fill-amber-400" strokeWidth={1.5} />
            </span>
          )}
        </span>
      );
    })}
  </span>
);

export default TeamPage;
